import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

const menuItems = [
  { icon: "ri-dashboard-3-line", label: "대시보드", path: "/admin" },
  { icon: "ri-user-3-line", label: "회원 관리", path: "/admin/users" },
  { icon: "ri-coin-line", label: "충전 내역", path: "/admin/charges" },
  { icon: "ri-send-plane-line", label: "발송 통계", path: "/admin/send-stats" },
  { icon: "ri-shield-check-line", label: "발신번호 심사", path: "/admin/sender-review" },
  { icon: "ri-smartphone-line", label: "3사 테스트 심사", path: "/admin/carrier-test-review" },
  { icon: "ri-notification-3-line", label: "공지사항 관리", path: "/admin/notices" },
  { icon: "ri-key-2-line", label: "API 키 관리", path: "/admin/api-keys" },
  { icon: "ri-smartphone-line", label: "3사 수신번호", path: "/admin/carrier-numbers" },
  { icon: "ri-settings-3-line", label: "시스템 설정", path: "/admin/system" },
];

interface Props {
  collapsed: boolean;
  onToggle: () => void;
}

export default function AdminSidebar({ collapsed, onToggle }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [reviewCount, setReviewCount] = useState(0);
  const [chargeCount, setChargeCount] = useState(0);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate("/");
    } catch (e) {
      console.error("[AdminSidebar] 로그아웃 실패:", e);
    } finally {
      setSigningOut(false);
    }
  };

  const getUserInitial = () => {
    const name = user?.user_metadata?.full_name as string | undefined;
    if (name) return name.charAt(0).toUpperCase();
    return (user?.email ?? "A").charAt(0).toUpperCase();
  };

  const getUserName = () => {
    const name = user?.user_metadata?.full_name as string | undefined;
    return name ?? user?.email ?? "";
  };

  const fetchCount = async () => {
    try {
      const { data } = await supabase.functions.invoke("get-admin-review-count");
      if (data) {
        setReviewCount(data.carrier_count || 0);
        setChargeCount(data.charge_count || 0);
      }
    } catch (e) {
      console.error("Sidebar review count fetch failed:", e);
    }
  };

  useEffect(() => {
    fetchCount();

    const channel = supabase
      .channel("admin-notifications")
      .on("broadcast", { event: "new-carrier-test" }, fetchCount)
      .on("broadcast", { event: "review-completed" }, fetchCount)
      .on("broadcast", { event: "new-charge-request" }, fetchCount)
      .on("broadcast", { event: "charge-reviewed" }, fetchCount)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-[#0d0d1a] border-r border-white/[0.07] flex flex-col transition-all duration-300 z-40 ${
        collapsed ? "w-16" : "w-60"
      }`}
    >
      {/* Logo */}
      <div className={`h-16 flex items-center border-b border-white/[0.07] shrink-0 ${collapsed ? "justify-center px-0" : "px-5 gap-2.5"}`}>
        <Link to="/admin" className="flex items-center gap-2.5 cursor-pointer">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 text-white font-bold text-sm shrink-0">
            A
          </div>
          {!collapsed && (
            <div>
              <span className="text-white font-semibold text-sm whitespace-nowrap">Signal SMS</span>
              <span className="block text-rose-400 text-[10px] font-medium leading-none mt-0.5">관리자 콘솔</span>
            </div>
          )}
        </Link>
      </div>

      {/* Toggle button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-[72px] w-6 h-6 flex items-center justify-center bg-[#1a1a2e] border border-white/[0.12] rounded-full text-gray-400 hover:text-white cursor-pointer transition-colors z-50"
      >
        <i className={`${collapsed ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} text-xs`}></i>
      </button>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {!collapsed && (
          <p className="text-gray-600 text-[10px] font-semibold uppercase tracking-widest px-5 mb-2">메뉴</p>
        )}
        <div className={`${collapsed ? "px-2" : "px-3"} space-y-0.5`}>
          {menuItems.map((item) => {
            const isActive =
              item.path === "/admin"
                ? location.pathname === "/admin"
                : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 rounded-lg transition-all duration-150 cursor-pointer whitespace-nowrap group ${
                  collapsed ? "px-0 py-2.5 justify-center" : "px-3 py-2.5"
                } ${
                  isActive
                    ? "bg-gradient-to-r from-rose-600/30 to-orange-600/20 text-white border border-rose-500/20"
                    : "text-gray-400 hover:text-white hover:bg-white/[0.05]"
                }`}
                title={collapsed ? item.label : undefined}
              >
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-base ${isActive ? "text-rose-400" : "text-current"}`}></i>
                </div>
                {!collapsed && <span className="text-sm">{item.label}</span>}
                {!collapsed && item.path === "/admin/carrier-test-review" && reviewCount > 0 && (
                  <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center bg-rose-500 text-white text-[10px] font-bold rounded-full px-1">
                    {reviewCount}
                  </span>
                )}
                {!collapsed && item.path === "/admin/charges" && chargeCount > 0 && (
                  <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center bg-amber-500 text-white text-[10px] font-bold rounded-full px-1">
                    {chargeCount}
                  </span>
                )}
                {isActive && !collapsed && !(item.path === "/admin/carrier-test-review" && reviewCount > 0) && !(item.path === "/admin/charges" && chargeCount > 0) && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-gradient-to-r from-rose-400 to-orange-400 shrink-0"></span>
                )}
              </Link>
            );
          })}
        </div>

        <div className="mx-4 my-4 h-px bg-white/[0.06]"></div>

        {/* Back to user dashboard */}
        <div className={`${collapsed ? "px-2" : "px-3"}`}>
          <Link
            to="/dashboard"
            className={`flex items-center gap-3 rounded-lg transition-all duration-150 cursor-pointer whitespace-nowrap text-gray-500 hover:text-white hover:bg-white/[0.05] ${
              collapsed ? "px-0 py-2.5 justify-center" : "px-3 py-2.5"
            }`}
            title={collapsed ? "사용자 대시보드" : undefined}
          >
            <div className="w-4 h-4 flex items-center justify-center shrink-0">
              <i className="ri-arrow-left-circle-line text-base"></i>
            </div>
            {!collapsed && <span className="text-sm">사용자 대시보드</span>}
          </Link>
        </div>
      </nav>

      {/* User profile */}
      <div className="border-t border-white/[0.07] p-3 shrink-0">
        {collapsed ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-orange-500 text-white text-xs font-semibold">
              {getUserInitial()}
            </div>
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-red-400 cursor-pointer transition-colors"
              title="로그아웃"
            >
              <i className="ri-logout-box-r-line text-sm"></i>
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-orange-500 text-white text-xs font-semibold shrink-0">
              {getUserInitial()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-medium truncate">{getUserName()}</p>
              <p className="text-rose-400 text-xs">관리자</p>
            </div>
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-red-400 cursor-pointer transition-colors shrink-0"
              title="로그아웃"
            >
              {signingOut ? (
                <i className="ri-loader-4-line animate-spin text-sm"></i>
              ) : (
                <i className="ri-logout-box-r-line text-sm"></i>
              )}
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}